import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { ConfirmationModal } from "./ConfirmationModal";

/* ---------- Layout ---------- */
const OptionsBox = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  overflow: hidden;
  height: 300px;
  align-items: center;
  width: 100%;
  background-color: white;
`;

const OptionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  overflow: auto;
  height: 100%;
  align-items: center;
  width: 100%;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #a0a0a0;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #909090;
  }
  scrollbar-width: thin;
  scrollbar-color: #a0a0a0 transparent;
`;

/* NOTE: use a transient prop $disabled so it doesn’t hit the DOM */
const OptionBox = styled.div`
  display: flex;
  color: black;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 80px;
  height: 60px;
  margin: 10px;
  position: relative;
  background-color: #f8f8f8;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

/* --- Indicator line with your priority --- */
const InnerLine = styled.div`
  width: 30%;
  height: 4px;
  border-radius: 20%;
  position: absolute;
  bottom: 15px;

  background-color: ${({
    $disabled,
    $isFull,
    $surpasses_people_limit,
    $from_restaurant,
    $past,
  }) => {
    // ✅ if it can't be clicked, always red
    if ($disabled) return "#D94B4B";

    // otherwise keep your status colors
    if (($from_restaurant && $isFull) || $past) return "#c46127";
    if ($surpasses_people_limit) return "#d9c834";
    return "#41ab8b";
  }};
`;
/* --- Warning cards in the modal --- */
const WarningCard = styled.div`
  background: #f1f3f4;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  color: #2d2d2d;
`;
const WarningTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
  color: #1e1e1e;
`;
const WarningText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: #4a4f55;
`;

/* ---------- Helpers ---------- */
const getDateTimeInTimezone = (timezone) => {
  const now = new Date();
  const dateInTimezone = new Date(
    now.toLocaleString("en-US", { timeZone: timezone }),
  );
  const year = dateInTimezone.getFullYear();
  const month = String(dateInTimezone.getMonth() + 1).padStart(2, "0");
  const day = String(dateInTimezone.getDate()).padStart(2, "0");
  const hours = String(dateInTimezone.getHours()).padStart(2, "0");
  const minutes = String(dateInTimezone.getMinutes()).padStart(2, "0");
  return {
    dateObj: dateInTimezone,
    dateStr: `${year}-${month}-${day}`,
    timeStr: `${hours}:${minutes}`,
  };
};

/** Parse "HH:MM/<people>/[b]/[f]/[pt]" safely */
const parseSlot = (token) => {
  const parts = token.split("/");
  const hhmm = parts[0]; // "HH:MM"
  let maxPeopleFromToken = null; // for warning text
  let flags = [];

  if (parts.length >= 2) {
    const maybeNumber = Number(parts[1]);
    if (!Number.isNaN(maybeNumber)) {
      maxPeopleFromToken = parts[1]; // keep as string (could be big)
      flags = parts.slice(2);
    } else {
      flags = parts.slice(1);
    }
  }
  return { hhmm, maxPeopleFromToken, flags };
};

const formatDate = (date) => {
  if (!(date instanceof Date)) date = new Date(date);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isTimeInPast = (slotDate, slotTimeStr, todayDate, nowTimeStr) => {
  const slotDateStr = formatDate(slotDate);
  const todayStr = formatDate(todayDate);
  if (slotDateStr < todayStr) return true;
  if (slotDateStr === todayStr && slotTimeStr < nowTimeStr) return true;
  return false;
};

/* --- Build independent warnings (none hide others) --- */
const buildWarnings = ({
  isFull,
  surpassesLimit,
  past,
  slotMaxFromToken,
  showTableWarnings,
}) => {
  const list = [];

  if (showTableWarnings && isFull) {
    list.push({
      key: "no_tables",
      title: "Mesas indisponíveis",
      text:
        "Não há mesas disponíveis nesta sala e horário. \n" +
        "Ao continuar, não serão atribuídas mesas automaticamente.",
    });
  }

  if (showTableWarnings && surpassesLimit) {
    list.push({
      key: "people_limit",
      title: "Limite de pessoas por horário ultrapassado",
      text:
        `Cada horário disponível aceita até ${
          slotMaxFromToken ?? "?"
        } pessoas.\n` +
        "Ao continuar irá ultrapassar este limite pré-definido.\n\n" +
        "Esta definição pode ser alterada nos horários.",
    });
  }

  if (past) {
    list.push({
      key: "past_time",
      title: "Hora ultrapassada",
      text: "Já passa da hora selecionada.",
    });
  }

  return list;
};

/* ---------- Component ---------- */
function ChooseTime({
  options,
  onTimeChange,
  time,
  changeTime,
  currentZone,
  current_date,
  from_restaurant,
  people_per_time, // used for the small count under the time
  timezone,
}) {
  const [selectedTime, setSelectedTime] = useState(time || null); // base "HH:MM"
  const [currentOptions, setCurrentOptions] = useState([]);
  const [showModalWarning, setShowModalWarning] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [pendingBaseTime, setPendingBaseTime] = useState(null); // base "HH:MM" to commit

  const { dateStr: currentDate, timeStr: currentTime } =
    getDateTimeInTimezone(timezone);
  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails || { restaurants: {} };

  const selectedTimeRef = useRef(null);

  /* NEW: container + per-option refs */
  const listContainerRef = useRef(null);
  const optionRefs = useRef({}); // key by token to avoid collisions

  useEffect(() => {
    if (time !== selectedTime) setSelectedTime(time);
  }, [time]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (options && currentZone) {
      const timesInCurrentZone = options[currentZone] || [];
      setCurrentOptions(timesInCurrentZone);
    } else {
      setCurrentOptions([]);
    }
    // Reset refs map for fresh render
    optionRefs.current = {};
  }, [options, currentZone]);

  const isDisabledForUser = (token) => {
    const { hhmm, flags } = parseSlot(token);
    const hasB = flags.includes("b");
    const hasF = flags.includes("f");
    const hasPT = flags.includes("pt");

    const isPastClient = isTimeInPast(
      current_date,
      hhmm,
      currentDate,
      currentTime,
    );
    const past = hasPT || isPastClient;

    // non-restaurant users: disabled if any warning present
    return !from_restaurant && (hasB || hasF || past);
  };

  /* NEW: auto-scroll to first enabled time whenever the list / time context changes */
  useEffect(() => {
    if (!Array.isArray(currentOptions) || currentOptions.length === 0) return;

    const firstEnabledToken = currentOptions.find((t) => !isDisabledForUser(t));
    if (!firstEnabledToken) return;

    const el = optionRefs.current[firstEnabledToken];
    if (el && typeof el.scrollIntoView === "function") {
      // smooth keeps it nice; change to "auto" if you prefer instant jump
      el.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    currentOptions,
    current_date,
    currentDate,
    currentTime,
    timezone,
    from_restaurant,
  ]);

  const handleTimeClick = (event, token) => {
    event.preventDefault();

    const { hhmm, maxPeopleFromToken, flags } = parseSlot(token);
    setPendingBaseTime(hhmm);

    const hasB = flags.includes("b"); // limit surpassed
    const hasF = flags.includes("f"); // no tables / unavailable
    const hasPT = flags.includes("pt"); // past time (server)

    // client fallback for past time if server didn't flag
    const isPastClient = isTimeInPast(
      current_date,
      hhmm,
      currentDate,
      currentTime,
    );
    const past = hasPT || isPastClient;

    // DISABLE for non-restaurant users if any warning applies
    const disabledForCustomer = !from_restaurant && (hasB || hasF || past);
    if (disabledForCustomer) return;

    const showTableWarnings = !!from_restaurant;
    const warningsToShow = buildWarnings({
      isFull: hasF,
      surpassesLimit: hasB,
      past,
      slotMaxFromToken: maxPeopleFromToken,
      showTableWarnings,
    });

    if (warningsToShow.length > 0) {
      setWarnings(warningsToShow);
      setShowModalWarning(true);
      return;
    }

    // No warnings → proceed
    setSelectedTime(hhmm);
    changeTime(hhmm);
    onTimeChange(hhmm);
  };

  const handleConfirmWarning = () => {
    if (pendingBaseTime) {
      setSelectedTime(pendingBaseTime);
      changeTime(pendingBaseTime);
      onTimeChange(pendingBaseTime);
    }
    setShowModalWarning(false);
  };

  const confirmLabel =
    warnings.length === 1 && warnings[0].key === "past_time"
      ? "Continuar com a hora ultrapassada"
      : "Continuar";

  return (
    <OptionsBox>
      {Array.isArray(currentOptions) && currentOptions.length > 0 ? (
        <OptionsWrapper ref={listContainerRef}>
          {currentOptions.map((token, index) => {
            const { hhmm, flags } = parseSlot(token);
            const hasB = flags.includes("b");
            const hasF = flags.includes("f");
            const hasPT = flags.includes("pt");

            // client past check for disabling UI
            const isPastClient = isTimeInPast(
              current_date,
              hhmm,
              currentDate,
              currentTime,
            );
            const past = hasPT || isPastClient;

            const isSelected = selectedTime === hhmm;

            // non-restaurant users: disable if any warning present
            const disabled = !from_restaurant && (hasB || hasF || past);

            const mappedCount = people_per_time?.[hhmm] ?? 0;

            return (
              <OptionBox
                key={`${token}-${index}`}
                ref={(el) => {
                  // store this element by token for auto-scroll
                  optionRefs.current[token] = el;
                  if (isSelected) selectedTimeRef.current = el;
                }}
                onClick={(event) => handleTimeClick(event, token)}
                $disabled={disabled}
                title={disabled ? "Indisponível neste horário" : undefined}
              >
                <div>{hhmm}</div>
                {from_restaurant && mappedCount > 0 && (
                  <div
                    style={{
                      fontSize: "10px",
                      position: "absolute",
                      bottom: "0px",
                    }}
                  >
                    ({mappedCount})
                  </div>
                )}
                <InnerLine
                  $disabled={disabled}
                  $isFull={hasF}
                  $surpasses_people_limit={hasB}
                  $from_restaurant={from_restaurant}
                  $past={past}
                />
              </OptionBox>
            );
          })}
        </OptionsWrapper>
      ) : (
        <div>Selecionar sala</div>
      )}

      <ConfirmationModal
        topSpacing="200px auto"
        show={showModalWarning}
        onHide={() => setShowModalWarning(false)}
        onConfirm={handleConfirmWarning}
        onCancel={() => setShowModalWarning(false)}
        title="Aviso"
        body={
          <div>
            {warnings.map((w) => (
              <WarningCard key={w.key}>
                <WarningTitle>{w.title}</WarningTitle>
                <WarningText>
                  {w.text.split("\n").map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </WarningText>
              </WarningCard>
            ))}
          </div>
        }
        confirmLabel={confirmLabel}
        cancelLabel="Cancelar"
        confirmColor="#EE754C"
        cancelColor="#99B0AD"
      />
    </OptionsBox>
  );
}

export default ChooseTime;
